import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../context/app.context";
import orderOptimizerUserPermissionsHelper from "./common/user-permissions/order-optimizer-user-permissions.helper";
import OrderOptimizerViewModel from "./common/types/order-optimizer-view-model";
import OrderOptimizerTaxiDetailsComponent from "./taxi/details/order-optimizer-taxi-details.component";
import OrderOptimizerRailyDetailsComponent from "./raily/details/order-optimizer-raily-details.component";
import OrderOptimizerCargoDetailsComponent from "./cargo/details/order-optimizer-cargo-details.component";

const OrderOptimizerDetailsComponent: FC = () => {
  const { user, selectedAppLanguage } = useAppContext();

  const userPermissions = useMemo(
    () => orderOptimizerUserPermissionsHelper.getPermissions(user?.roles!),
    []
  );

  const [selectedViewMode, setSelectedViewMode] =
    useState<OrderOptimizerViewModel | null>(null);

  const viewOptions = useMemo(
    () => [
      {
        mode: OrderOptimizerViewModel.RAILY,
        component: <OrderOptimizerRailyDetailsComponent />,
        hasPermission: userPermissions.hasAccessToRailyView,
      },
      {
        mode: OrderOptimizerViewModel.CARGO,
        component: <OrderOptimizerCargoDetailsComponent />,
        hasPermission: userPermissions.hasAccessToCargoView,
      },
      {
        mode: OrderOptimizerViewModel.TAXI,
        component: <OrderOptimizerTaxiDetailsComponent />,
        hasPermission: userPermissions.hasAccessToTaxiView,
      },
    ],
    [userPermissions]
  );

  const getPossibleViewOptions = useCallback(() => {
    return viewOptions.filter((option) => option.hasPermission);
  }, [viewOptions]);

  const possibleViewOptions = useMemo(
    () => getPossibleViewOptions(),
    [getPossibleViewOptions]
  );

  useEffect(() => {
    if (possibleViewOptions.length === 0) {
      return;
    }
    setSelectedViewMode(possibleViewOptions[0].mode);
  }, [possibleViewOptions]);

  const SelectedViewComponent = useMemo(
    () =>
      viewOptions.find((option) => option.mode === selectedViewMode)?.component,
    [selectedViewMode, viewOptions]
  );

  if (!SelectedViewComponent) {
    return null;
  }

  return SelectedViewComponent;
};

export default OrderOptimizerDetailsComponent;
